import { DeleteOutlined } from '@icon';
import { Table, Tag } from '@ui';
import dayjs from 'dayjs';
import React from 'react';
import { useIntl } from 'react-intl';
import type { ColumnWithLooseAccessor, ColumnWithStrictAccessor } from 'react-table';
import { useTheme } from 'styled-components';
import { TMyProvider } from 'types/software-provider.types';
import { getShortDateFormat, getTimeFormat } from '../../../../helpers/utils-helpers';

type TColumn = ColumnWithLooseAccessor<TMyProvider> | ColumnWithStrictAccessor<TMyProvider>;

interface GetTableColumnParams {
  onDeleteClick: (event: React.MouseEvent<HTMLElement>, rowValues: TMyProvider) => void;
  canEdit?: boolean;
}

export function useTableColumns(params: GetTableColumnParams) {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const { onDeleteClick, canEdit } = params;

  return React.useMemo<TColumn[]>(
    () => [
      {
        Header: formatMessage({ id: 'departments_table_title' }),
        id: 'name',
        accessor: 'name',
      },
      {
        Header: 'Názov služby',
        id: 'merchant',
        accessor: 'merchant',
        Cell: ({ row }) => {
          return <>{row.original.merchant.name}</>;
        },
      },
      {
        Header: 'Status',
        id: 'status',
        accessor: 'status',
        Cell: ({ row }) => {
          if (row.original.status === 'approved') {
            return <Tag variant="success">Prepojené</Tag>;
          } else if (row.original.status === 'pending') {
            return <Tag variant="info">Prebieha prepojenie</Tag>;
          }
          return <Tag variant="danger">Prepojenie zrušené</Tag>;
        },
      },
      {
        Header: 'ID požiadavky',
        id: 'requestId',
        accessor: 'requestId',
      },
      {
        Header: formatMessage({ id: 'staff_table_created' }),
        accessor: 'createdAt',
        id: 'createdAt',

        Cell: ({ row }: any) => {
          return (
            <span>
              {dayjs(row.original.createdAt).format(`${getShortDateFormat()} ${getTimeFormat()}`)}
            </span>
          );
        },
      },
      {
        Header: '',
        id: 'edit',
        Cell: ({ row }) => {
          if (!canEdit || row.original.status !== 'pending') {
            return null;
          }

          return (
            <Table.Actions
              items={[
                {
                  key: 'delete',
                  variant: 'danger',
                  label: formatMessage({ id: 'table_context_menu_remove' }),
                  Icon: DeleteOutlined,
                  onClick: (e) => onDeleteClick(e.domEvent as any, row.original),
                },
              ]}
            />
          );
        },
      } as ColumnWithLooseAccessor<TMyProvider>,
    ],
    [formatMessage, onDeleteClick, theme, canEdit],
  );
}
